"use client";

import * as React from "react";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";
import { PortalScopeProvider } from "../../lib/portal-scope";
import { Button } from "./button";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "./sheet";

// ---------------------------------------------------------------------------
// Secondary sheet context
// ---------------------------------------------------------------------------

type SecondarySheetContextValue = {
	isSecondaryOpen: boolean;
	setIsSecondaryOpen: (isOpen: boolean) => void;
	onSecondaryClose: (() => void) | null;
	setOnSecondaryClose: (onClose: (() => void) | null) => void;
};

const SecondarySheetContext = React.createContext<
	SecondarySheetContextValue | undefined
>(undefined);

export function useSecondarySheetContext():
	| SecondarySheetContextValue
	| undefined {
	return React.useContext(SecondarySheetContext);
}

// ---------------------------------------------------------------------------
// StandardSheet root
// ---------------------------------------------------------------------------

/** Props for the {@link StandardSheet} compound component. */
export type StandardSheetProps = {
	/** Whether the sheet is currently visible. */
	isShown: boolean;
	/** Callback invoked when the sheet requests to close (overlay click, close button, or Escape). */
	onClose: () => void;
	/** Content rendered inside the scrollable body area. */
	children: React.ReactNode;
	/** Heading text displayed in the colored header bar. */
	title: string;
	/** Optional content pinned to the bottom of the sheet (e.g. {@link StandardSheet.FooterActions}). */
	footer?: React.ReactNode;
	/** Controls the maximum width of the sheet panel. Defaults to `"standard"`. */
	variant?: "standard" | "wide-screen" | "always-wide-screen";
	/** Optional classes merged onto the colored header bar, e.g. to override its background. Defaults to `bg-sheet-header` (the brand colour in light). */
	headerClassName?: string;
	/** Optional classes merged onto the scrollable body area, e.g. to tint its background. Defaults to none. */
	bodyClassName?: string;
	/**
	 * Reserves bottom padding on the scrollable content area when the sheet has no `footer`,
	 * so long content clears floating overlays pinned to the bottom-right (e.g. the live-chat launcher).
	 * Has no effect when a `footer` is supplied (the footer already reserves bottom space). Defaults to `true`.
	 */
	reserveBottomSpace?: boolean;
	/** Test id set on the sheet panel, so it covers the header, body and footer. */
	"data-testid"?: string;
};

const standardSheetContentVariants = cva(
	"flex w-full flex-col gap-0 overflow-y-auto py-0 !border-none px-0 z-[5002]",
	{
		variants: {
			variant: {
				standard: "sm:max-w-lg",
				"wide-screen": "sm:max-w-3xl",
				"always-wide-screen": "sm:max-w-5xl",
			},
		},
		defaultVariants: {
			variant: "standard",
		},
	},
);

function StandardSheetRoot({
	isShown,
	onClose,
	title,
	children,
	footer,
	variant = "standard",
	headerClassName,
	bodyClassName,
	reserveBottomSpace = true,
	"data-testid": testId,
}: StandardSheetProps) {
	const contentRef = React.useRef<HTMLDivElement>(null);
	const [hasOverflow, setHasOverflow] = React.useState(false);
	const [delayedIsShown, setDelayedIsShown] = React.useState(false);
	const [isSecondaryOpen, setIsSecondaryOpen] = React.useState(false);
	const [onSecondaryClose, setOnSecondaryClose] = React.useState<
		(() => void) | null
	>(null);

	const [portalContainer, setPortalContainer] =
		React.useState<HTMLElement | null>(null);

	const sheetContentRef = React.useCallback((node: HTMLDivElement | null) => {
		contentRef.current = node;
		setPortalContainer(node);
	}, []);

	React.useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout>;

		if (isShown) {
			timeoutId = setTimeout(() => {
				setDelayedIsShown(true);
			}, 100);
		} else {
			if (isSecondaryOpen && onSecondaryClose) {
				onSecondaryClose();
			} else {
				timeoutId = setTimeout(() => {
					setDelayedIsShown(false);
				}, 100);
			}
		}

		return () => {
			clearTimeout(timeoutId);
		};
	}, [isShown, isSecondaryOpen, onSecondaryClose]);

	React.useEffect(() => {
		if (!isShown && !isSecondaryOpen && delayedIsShown) {
			const timeoutId = setTimeout(() => {
				setDelayedIsShown(false);
			}, 100);

			return () => {
				clearTimeout(timeoutId);
			};
		}
	}, [isShown, isSecondaryOpen, delayedIsShown]);

	React.useEffect(() => {
		const el = contentRef.current;
		if (!el) {
			return;
		}

		const handleResize = () => {
			setHasOverflow(el.scrollHeight > el.clientHeight);
		};

		const observer = new ResizeObserver(handleResize);

		// Delay to allow the sheet enter animation to finish before measuring.
		// Tracks the `duration-panel-in` token the sheet opens on, so keep the two in step.
		const timeoutId = setTimeout(() => {
			handleResize();
			observer.observe(el);
		}, 300);

		return () => {
			clearTimeout(timeoutId);
			observer.disconnect();
		};
	}, [delayedIsShown]);

	const contextValue: SecondarySheetContextValue = {
		isSecondaryOpen,
		setIsSecondaryOpen,
		onSecondaryClose,
		setOnSecondaryClose,
	};

	return (
		<SecondarySheetContext.Provider value={contextValue}>
			<Sheet
				open={delayedIsShown}
				onOpenChange={(open) => {
					if (!open) {
						onClose();
					}
				}}
			>
				<SheetContent
					className={standardSheetContentVariants({ variant })}
					ref={sheetContentRef}
					isOpen={delayedIsShown}
					displayCloseButton={false}
					data-testid={testId}
				>
					<PortalScopeProvider container={portalContainer}>
						<div className="flex-1">
							<div
								className={cn(
									"px-6 bg-sheet-header text-sheet-header-foreground h-[var(--sheet-header-height,60px)] flex items-center justify-between",
									headerClassName,
								)}
							>
								<SheetTitle className="text-[15px] font-bold leading-normal text-sheet-header-foreground">
									{title}
								</SheetTitle>
								<SheetClose>
									<X className="h-4 w-4" strokeWidth={3} />
									<span className="sr-only">Close</span>
								</SheetClose>
							</div>
							<div
								className={cn(
									"px-6 pt-6",
									!footer && reserveBottomSpace && "pb-24",
									bodyClassName,
								)}
								data-testid="standard-sheet-body"
							>
								{children}
							</div>
						</div>
					</PortalScopeProvider>

					{footer && (
						<div
							className={cn(
								"w-full shrink-0 bg-popover sticky bottom-0 filter px-6",
								hasOverflow
									? "drop-shadow-[0_-6px_6px_var(--color-sheet-footer-shadow)]"
									: "drop-shadow-[0_-6px_6px_rgba(0,0,0,0.0)]",
							)}
						>
							{footer}
						</div>
					)}
				</SheetContent>
			</Sheet>
		</SecondarySheetContext.Provider>
	);
}

// ---------------------------------------------------------------------------
// FooterActions
// ---------------------------------------------------------------------------

/** Props for {@link StandardSheet.FooterActions}, a Cancel/Confirm (or Back/Next) button pair. */
export type SheetFooterActionsProps = {
	/** Shows a spinner on the confirm button and disables cancel. */
	isLoading?: boolean;
	/** Whether the form has been modified. Used with `disableSubmitUntilDirty`. */
	isDirty?: boolean;
	/** Callback for the cancel (or back) button. */
	onClose: () => void;
	/** Callback for the confirm (or next) button. */
	onConfirm?: () => void;
	/** Callback for the back button in multi-step flows. */
	onBack?: () => void;
	/** When `true`, renders "Next" instead of "Confirm" and enables multi-step navigation. */
	hasNext?: boolean;
	/** Disable the confirm button until the form is dirty. Defaults to `true`. */
	disableSubmitUntilDirty?: boolean;
	/** Disable the confirm button until the form is valid. Defaults to `true`. */
	disabledSubmitUntilValid?: boolean;
	/** Unconditionally disable the confirm button. */
	disabled?: boolean;
	/** Whether the form is currently valid. Used with `disabledSubmitUntilValid`. */
	isValid?: boolean;
};

function SheetFooterActions({
	isLoading = false,
	isDirty = true,
	onClose,
	onConfirm,
	onBack,
	disableSubmitUntilDirty = true,
	disabledSubmitUntilValid = true,
	hasNext = false,
	disabled = false,
	isValid = true,
}: SheetFooterActionsProps) {
	const isLastStep = !hasNext && Boolean(onBack);

	return (
		<div className="flex gap-4 pt-4 pb-6">
			<Button
				size="lg"
				className="flex-1"
				type="button"
				variant="outline"
				onClick={isLastStep ? onBack : onClose}
				disabled={isLoading}
			>
				{isLastStep ? "Back" : "Cancel"}
			</Button>

			<Button
				size="lg"
				className="flex-1"
				variant="primary"
				type="submit"
				disabled={
					(disableSubmitUntilDirty && !isDirty) ||
					disabled ||
					(disabledSubmitUntilValid && !isValid)
				}
				loading={isLoading}
				onClick={onConfirm}
				aria-label={hasNext ? "Next" : "Submit form"}
			>
				{hasNext ? "Next" : "Confirm"}
			</Button>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const StandardSheet = Object.assign(StandardSheetRoot, {
	FooterActions: SheetFooterActions,
});

export type { SheetFooterActionsProps as StandardSheetFooterActionsProps };
